import { Divider } from 'primereact/divider'
import { Dropdown } from 'primereact/dropdown'
import { AlarmCriterion } from '../types/alarm-criterion'
import { APIAlarmCriteria } from '../types/api-alarm-criteria'
import { categoryOptions } from '../types/alarm-consts'
import { AlarmCategory, AnprCriterionTypeEnum } from '../types/alarm.types'
import { getCriterionConfig } from '../config/criterion-config'
import { ColorSelector } from './selectors/color-selector'
import { BooleanSelector } from './selectors/boolean-selector'
import { TextInputSelector } from './selectors/text-input-selector'
import { DropdownSelector } from './selectors/dropdown-selector'

interface AlarmCriteriaFormProps {
  alarmCriterion: AlarmCriterion
  criteria: AlarmCriterion[]
  anprAlarmCriteria: APIAlarmCriteria[]
  ahcrAlarmCriteria: APIAlarmCriteria[]
  onCriterionChange: (criterion: AlarmCriterion) => void
  onCategoryChange: (category: AlarmCategory) => void
  onAddCriterion: () => void
}

const AlarmCriteriaForm: React.FC<AlarmCriteriaFormProps> = ({
  alarmCriterion,
  criteria,
  ahcrAlarmCriteria,
  anprAlarmCriteria,
  onCriterionChange,
  onCategoryChange,
  onAddCriterion,
}) => {
  const getCurrentTypeOptions = () => {
    return alarmCriterion.category === 'ANPR'
      ? anprAlarmCriteria
      : ahcrAlarmCriteria
  }

  const selectedCategoryTemplate = (object: any) => {
    if (!object) return null
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <i className={object.icon} />
        <span>{object.label}</span>
      </div>
    )
  }

  const categoryOptionTemplate = (object: any) => {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <i className={object.icon} />
        <span>{object.label}</span>
      </div>
    )
  }

  const handleValueChange = (value: string, displayValue: string) => {
    console.log('value: ', value, ' displayValue: ', displayValue)
    onCriterionChange({
      ...alarmCriterion,
      value,
      displayValue,
    })
  }

  const handleTextChange = (value: string) => {
    onCriterionChange({
      ...alarmCriterion,
      value,
    })
  }

  const renderCriterionInput = () => {
    const config = getCriterionConfig(alarmCriterion.category, alarmCriterion.type)

    if (!config) return null

    switch (config.component) {
      case 'color':
        return (
          <ColorSelector
            label={config.label}
            icon={config.icon}
            value={alarmCriterion.value}
            onChange={handleValueChange}
            onAdd={onAddCriterion}
          />
        )

      case 'text':
        return (
          <TextInputSelector
            label={config.label}
            icon={config.icon}
            value={alarmCriterion.value}
            placeholder={config.placeholder}
            maxLength={config.maxLength}
            showPlatePreview={alarmCriterion.type === AnprCriterionTypeEnum.LICENSE_PLATE}
            onChange={handleTextChange}
            onAdd={onAddCriterion}
          />
        )

      case 'boolean':
        return (
          <BooleanSelector
            label={config.label}
            icon={config.icon}
            value={alarmCriterion.value}
            onChange={handleValueChange}
            onAdd={onAddCriterion}
          />
        )

      case 'dropdown':
        return (
          <DropdownSelector
            label={config.label}
            icon={config.icon}
            value={alarmCriterion.value}
            options={config.options || []}
            onChange={handleValueChange}
            onAdd={onAddCriterion}
          />
        )

      default:
        return null
    }
  }

  return (
    <div className='alarms-form-left'>
      {/* Category Selection */}
      <div
        className='alarms-form-section'
        style={{ marginBottom: '1.5rem' }}
      >
        <h4 className='alarms-section-title'>
          <i className='pi pi-flag' />
          Categoria do Alarme
        </h4>
        <Divider />
        <div className='alarms-field'>
          <Dropdown
            value={alarmCriterion.category}
            options={categoryOptions}
            onChange={(e) => onCategoryChange(e.value)}
            valueTemplate={selectedCategoryTemplate}
            itemTemplate={categoryOptionTemplate}
            optionLabel='label'
            placeholder='Selecione a categoria'
            className='alarms-dropdown'
            style={{ width: '100%' }}
            disabled={criteria.length > 0}
            tooltip={
              criteria.length > 0
                ? 'Remova todos os critérios para alterar a categoria'
                : undefined
            }
            tooltipOptions={{ position: 'top' }}
          />
          <small
            style={{
              display: 'block',
              marginTop: '0.5rem',
              color: criteria.length > 0 ? '#fbbf24' : '#9ca3af',
            }}
          >
            {criteria.length > 0 ? (
              <>
                <i
                  className='pi pi-lock'
                  style={{ marginRight: '0.5rem' }}
                />
                Categoria bloqueada - Remova os critérios para alterar
              </>
            ) : alarmCriterion.category === 'ANPR' ? (
              'Monitoramento de Veículos (Placas e Cores)'
            ) : (
              'Monitoramento de Pessoas (Gênero e Roupas)'
            )}
          </small>
        </div>
      </div>

      {/* Add New Criterion Section */}
      <div className='alarms-form-section'>
        <h4 className='alarms-section-title'>
          <i className='pi pi-plus-circle' />
          {criteria.length > 0
            ? 'Adicionar Mais Critérios'
            : 'Adicionar Critério'}
        </h4>
        <Divider />

        <div className='alarms-field'>
          <label
            htmlFor='type'
            className='alarms-label'
          >
            <i className='pi pi-tag' />
            Tipo de Critério
          </label>
          <Dropdown
            id='type'
            value={alarmCriterion.type}
            options={getCurrentTypeOptions()}
            onChange={(e) => {
              onCriterionChange({
                ...alarmCriterion,
                type: e.value,
                value: '',
              })
            }}
            optionLabel='displayValue'
            optionValue='key'
            placeholder='Escolha o tipo de critério'
            className='alarms-dropdown'
            style={{ width: '100%' }}
          />
        </div>

        {renderCriterionInput()}
      </div>
    </div>
  )
}

export default AlarmCriteriaForm
